/*
  <product-card-v2>
  Controller for the redesigned collection product card.
  Responsibilities:
    - Quantity stepper (+ / -)
    - Weight <select> -> keep [name="id"] in sync, update the visible + tab prices
    - One-time / Subscribe toggle -> show the frequency select and toggle the
      `selling_plan` field so the line is added as a subscription (native selling
      plans, e.g. Seal Subscriptions)
    - Frequency <select> -> update the Subscribe tab price
    - Wishlist heart toggle
    - Post-add "Added" feedback

  Add-to-cart is submitted by the theme's <product-form> element (product-form.js),
  so cart drawer / AJAX behaviour is inherited unchanged.
*/
if (!customElements.get('product-card-v2')) {
  class ProductCardV2 extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('.pcv2__select select');
      this.idInput = this.querySelector('[data-pcv2-id]');
      this.qtyInput = this.querySelector('.pcv2__qty-input');
      this.priceAmount = this.querySelector('.pcv2__price-amount');
      this.priceFrom = this.querySelector('.pcv2__price-from');
      this.priceWas = this.querySelector('.pcv2__price-was');
      this.atc = this.querySelector('.pcv2__atc');
      this.wish = this.querySelector('.pcv2__wish');

      // Subscription bits (present only when the product has selling plans).
      this.modeInputs = this.querySelectorAll('.pcv2__mode');
      this.buyRow = this.querySelector('.pcv2__buy');
      this.freqWrap = this.querySelector('.pcv2__freq');
      this.freqSelect = this.querySelector('.pcv2__freq-select');
      this.segOnetime = this.querySelector('[data-pcv2-onetime]');
      this.segSubs = this.querySelector('[data-pcv2-subs]');
      this.segBadge = this.querySelector('[data-pcv2-badge]');
      this.subsData = this.parseSubsData();

      // connectedCallback re-runs every time the element is moved or
      // re-inserted (grid search appends, filter re-renders, carousels).
      // Element listeners are bound once; later connects only re-attach the
      // document listener dropped in disconnectedCallback and refresh state —
      // otherwise the stepper handlers stack and one click adds 2, 3, 4…
      this.listenFavorites();
      if (this._bound) {
        this.syncWishFromStore();
        return;
      }
      this._bound = true;

      this.bindQuantity();
      this.bindSelect();
      this.bindWishlist();
      this.bindAddedFeedback();
      this.bindPurchaseMode();
    }

    parseSubsData() {
      const el = this.querySelector('.pcv2-subs-data');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    getVariantId() {
      if (this.select) return this.select.value;
      if (this.idInput) return this.idInput.value;
      return null;
    }

    /* ---------- Quantity stepper ---------- */
    bindQuantity() {
      if (!this.qtyInput) return;
      const min = parseInt(this.qtyInput.min, 10) || 1;

      this.querySelectorAll('.pcv2__qty-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = btn.dataset.pcv2Step === 'down' ? -1 : 1;
          const current = parseInt(this.qtyInput.value, 10) || min;
          const next = Math.max(min, current + step);
          this.qtyInput.value = next;
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      this.qtyInput.addEventListener('change', () => {
        const val = parseInt(this.qtyInput.value, 10);
        if (Number.isNaN(val) || val < min) this.qtyInput.value = min;
      });
    }

    /* ---------- Variant <select> ---------- */
    bindSelect() {
      if (!this.select) return;
      this.select.addEventListener('change', () => {
        const opt = this.select.options[this.select.selectedIndex];
        if (!opt) return;

        if (this.idInput) this.idInput.value = opt.value;

        // Update the visible (one-time) price.
        if (this.priceAmount && opt.dataset.price) {
          this.priceAmount.textContent = opt.dataset.price;
        }
        // Toggle's one-time price: updatePurchasePrices() covers cards WITH
        // selling-plan data; without it (disabled Subscribe segment) sync here.
        if (!this.subsData && this.segOnetime && opt.dataset.price) {
          this.segOnetime.textContent = opt.dataset.price;
        }
        if (this.priceFrom) this.priceFrom.hidden = true;
        if (this.priceWas) {
          if (opt.dataset.compare) {
            this.priceWas.textContent = opt.dataset.compare;
            this.priceWas.hidden = false;
          } else {
            this.priceWas.hidden = true;
          }
        }

        // Reflect availability on the button.
        if (this.atc) {
          const unavailable = opt.dataset.available === 'false';
          this.atc.toggleAttribute('disabled', unavailable);
          const label = this.atc.querySelector('.pcv2__atc-label');
          if (label) {
            label.textContent = unavailable
              ? (this.dataset.soldOutText || 'Sold out')
              : (this.dataset.addText || 'Add to Cart');
          }
        }

        // Update the purchase-toggle prices for the new variant.
        this.updatePurchasePrices();
      });
    }

    /* ---------- Purchase mode (one-time / subscribe) ---------- */
    bindPurchaseMode() {
      if (!this.modeInputs || this.modeInputs.length === 0) return;

      this.modeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) this.applyMode(input.value);
        });
      });

      if (this.freqSelect) {
        this.freqSelect.addEventListener('change', () => this.updatePurchasePrices());
      }

      // Initialise from the currently-checked mode.
      const checked = this.querySelector('.pcv2__mode:checked');
      this.applyMode(checked ? checked.value : 'onetime');
    }

    applyMode(mode) {
      const subscribe = mode === 'subscribe';
      if (this.freqWrap) this.freqWrap.hidden = !subscribe;
      if (this.freqSelect) this.freqSelect.disabled = !subscribe; // disabled => not submitted (one-time)
      if (this.buyRow) this.buyRow.classList.toggle('is-subscribe', subscribe);
      this.updatePurchasePrices();
    }

    updatePurchasePrices() {
      if (!this.subsData) return;
      const variant = this.subsData[this.getVariantId()];
      if (!variant) return;

      if (this.segOnetime && variant.onetime) {
        this.segOnetime.textContent = variant.onetime;
      }

      const planId = this.freqSelect ? this.freqSelect.value : null;
      const plan = planId && variant.plans ? variant.plans[planId] : null;
      if (plan) {
        if (this.segSubs) this.segSubs.textContent = plan.price;
        if (this.segBadge) {
          if (plan.percent > 0) {
            this.segBadge.textContent = `-${plan.percent}%`;
            this.segBadge.hidden = false;
          } else {
            this.segBadge.hidden = true;
          }
        }
      }
    }

    /* ---------- Wishlist heart (persisted via window.cibFavorites) ---------- */
    // Keep every instance of this product in sync (other cards, other
    // sections, other tabs). productId null = bulk change, re-check own state.
    // Re-attached on every connect (disconnectedCallback removes it).
    listenFavorites() {
      if (!this.wish || this._onFavoritesChange) return;
      this._onFavoritesChange = (e) => {
        const d = e.detail || {};
        if (d.productId === null || String(d.productId) === String(this.dataset.productId)) {
          this.syncWishFromStore();
        }
      };
      document.addEventListener('cib:favorites:change', this._onFavoritesChange);
    }

    bindWishlist() {
      if (!this.wish) return;

      // Restore the saved state whenever a card is rendered/connected.
      this.syncWishFromStore();

      this.wish.addEventListener('click', (e) => {
        e.preventDefault();
        let active;
        if (window.cibFavorites && this.dataset.productId) {
          active = window.cibFavorites.toggle(this.dataset.productId);
          this.setWishState(active); // own card updates instantly; peers via event
        } else {
          // Store unavailable — keep the original per-card visual toggle.
          active = this.wish.classList.toggle('is-active');
          this.wish.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
        this.dispatchEvent(
          new CustomEvent('pcv2:wishlist', {
            bubbles: true,
            detail: { productId: this.dataset.productId, active }
          })
        );
      });
    }

    setWishState(active) {
      if (!this.wish) return;
      this.wish.classList.toggle('is-active', !!active);
      this.wish.setAttribute('aria-pressed', active ? 'true' : 'false');
    }

    syncWishFromStore() {
      if (window.cibFavorites && this.dataset.productId) {
        this.setWishState(window.cibFavorites.has(this.dataset.productId));
      }
    }

    disconnectedCallback() {
      if (this._onFavoritesChange) {
        document.removeEventListener('cib:favorites:change', this._onFavoritesChange);
        this._onFavoritesChange = null;
      }
    }

    /* ---------- Post-add feedback ---------- */
    bindAddedFeedback() {
      this.addEventListener('on:cart:add', () => this.showAdded());
      this.addEventListener('on:line-item:change', () => this.showAdded());
    }

    showAdded() {
      this.classList.add('is-added');
      const label = this.atc && this.atc.querySelector('.pcv2__atc-label');
      if (label) {
        const original = this.dataset.addText || label.textContent;
        label.textContent = this.dataset.addedText || 'Added ✓';
        window.setTimeout(() => {
          label.textContent = original;
          this.classList.remove('is-added');
        }, 1800);
      }
    }
  }

  customElements.define('product-card-v2', ProductCardV2);
}

/*
  Grid heading search (Figma 525-6698) — FULL-CATALOG.
  The round button expands into an input that live-filters product cards by
  title. On first use, the remaining collection pages are fetched once via the
  Section Rendering API and their (real, Liquid-rendered) cards are appended to
  the grid hidden — so search covers the whole collection, and injected cards
  keep all behaviors (variant lock, subscriptions, bundle interception).
  Uses the `pgh2-hidden` class so it composes with the bundle variant filter
  (which uses the `hidden` attribute) without conflicts.
*/
(function () {
  const MAX_PAGES = 30; // safety cap (~360 products at 12/page)

  function init() {
    const wrap = document.querySelector('[data-pgh2-search]');
    if (!wrap) return;

    // The button is optional: the toolbar variant (Figma 941-9667) renders an
    // always-visible input; the legacy heading variant expands via the button.
    const btn = wrap.querySelector('.pgh2__search-btn');
    const input = wrap.querySelector('.pgh2__search-input');
    if (!input) return;

    const grid = document.querySelector('.main-products-grid__results');
    const ul = document.querySelector('.pcv2-grid');
    const sectionEl = document.querySelector('.shopify-section.cc-collection-products-v2')
      || (ul && ul.closest('.shopify-section'));
    const sectionId = sectionEl && sectionEl.id ? sectionEl.id.replace('shopify-section-', '') : null;
    const perPage = document.querySelectorAll('.pcv2-grid product-card-v2, .pcv2-grid product-card-v3').length;

    let query = '';
    let debounceTimer = null;
    let catalogState = 'idle'; // idle | loading | done | failed

    function normalize(s) {
      return String(s || '').toLowerCase().trim();
    }

    function cardTitle(card) {
      const el = card.querySelector('.pcv2__title, .pcv2__title-list, .pcv3__title');
      return normalize(el ? el.textContent : '');
    }

    function loadedProductIds() {
      const ids = new Set();
      document.querySelectorAll('.pcv2-grid product-card-v2, .pcv2-grid product-card-v3').forEach((c) => {
        if (c.dataset.productId) ids.add(c.dataset.productId);
      });
      return ids;
    }

    // Fetch the remaining collection pages once and append their cards hidden.
    async function loadFullCatalog() {
      if (catalogState !== 'idle' || !sectionId || !ul || !perPage) return;
      catalogState = 'loading';

      try {
        const container = document.querySelector('[data-num-results]');
        const total = Number((container && container.dataset.numResults) || 0);
        let pages = Math.ceil(total / perPage) || 1;
        if (pages > MAX_PAGES) {
          console.warn('[Grid search] collection has ' + pages + ' pages; searching the first ' + MAX_PAGES + '.');
          pages = MAX_PAGES;
        }

        const requests = [];
        for (let p = 2; p <= pages; p++) {
          const url = new URL(window.location.href);
          url.searchParams.set('page', String(p));
          url.searchParams.set('sections', sectionId);
          requests.push(
            fetch(url.toString(), { headers: { Accept: 'application/json' } })
              .then((r) => (r.ok ? r.json() : null))
              .then((j) => (j && j[sectionId]) || '')
              .catch(() => '')
          );
        }

        const htmls = await Promise.all(requests);
        const seen = loadedProductIds();

        htmls.forEach((html) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          doc.querySelectorAll('.pcv2-grid > li').forEach((li) => {
            const card = li.querySelector('product-card-v2, product-card-v3');
            if (!card) return; // skip promo tiles
            const id = card.dataset.productId;
            if (!id || seen.has(id)) return;
            seen.add(id);
            li.setAttribute('data-pgh2-extra', '');
            li.classList.add('pgh2-hidden');
            ul.appendChild(li); // upgrades custom elements on insertion
          });
        });

        catalogState = 'done';
      } catch (e) {
        catalogState = 'failed';
        console.warn('[Grid search] full-catalog load failed; searching loaded items only.', e);
      }
      apply();
    }

    function ensureMessage() {
      let msg = document.querySelector('.pgh2-noresults');
      if (!msg) {
        msg = document.createElement('p');
        msg.className = 'pgh2-noresults';
        msg.hidden = true;
        if (ul && ul.parentElement) ul.parentElement.insertBefore(msg, ul.nextSibling);
      }
      return msg;
    }

    function apply() {
      const cards = document.querySelectorAll('.pcv2-grid product-card-v2, .pcv2-grid product-card-v3');
      let visible = 0;

      cards.forEach((card) => {
        const li = card.closest('li') || card;
        const isExtra = li.hasAttribute('data-pgh2-extra');
        // Extras exist only for searching: with no query they stay hidden so
        // normal pagination is unaffected.
        const match = query ? cardTitle(card).indexOf(query) !== -1 : !isExtra;
        li.classList.toggle('pgh2-hidden', !match);
        // Count as visible only if the variant filter isn't hiding it either.
        if (match && !li.hidden) visible += 1;
      });

      const msg = ensureMessage();
      if (query && visible === 0) {
        msg.textContent = catalogState === 'loading'
          ? 'Searching all products…'
          : 'No products match "' + input.value.trim() + '".';
        msg.hidden = false;
      } else {
        msg.hidden = true;
      }
    }

    if (btn) {
      btn.addEventListener('click', () => {
        const opening = input.hidden;
        input.hidden = !opening ? true : false;
        btn.setAttribute('aria-expanded', opening ? 'true' : 'false');
        if (opening) {
          input.focus();
          loadFullCatalog(); // warm the catalog as soon as search is opened
        } else {
          // Collapsing clears the search.
          input.value = '';
          query = '';
          apply();
        }
      });
    }

    // Always-visible variant: warm the catalog on focus instead of on expand.
    input.addEventListener('focus', () => loadFullCatalog());

    input.addEventListener('input', () => {
      loadFullCatalog();
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        query = normalize(input.value);
        apply();
      }, 120);
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (btn) {
          btn.click();
        } else {
          input.value = '';
          query = '';
          apply();
        }
      }
    });

    // The facet AJAX replaced the grid: injected extras belong to the previous
    // filter state — drop them and refetch (under the new URL) if a search is
    // active.
    document.addEventListener('on:facet-filters:updated', () => {
      document.querySelectorAll('[data-pgh2-extra]').forEach((li) => li.remove());
      catalogState = 'idle';
      if (query) loadFullCatalog();
      apply();
    });

    // Re-apply after pagination / infinite scroll adds items, and drop any
    // injected duplicate when the theme loads the same product naturally.
    if (grid && window.MutationObserver) {
      const observer = new MutationObserver((mutations) => {
        let added = false;
        mutations.forEach((m) => {
          m.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            added = true;
            node.querySelectorAll
              && node.querySelectorAll('product-card-v2, product-card-v3').forEach((card) => {
                const li = card.closest('li');
                if (!li || li.hasAttribute('data-pgh2-extra')) return;
                const id = card.dataset.productId;
                if (!id) return;
                document
                  .querySelectorAll('[data-pgh2-extra] product-card-v2[data-product-id="' + id + '"], [data-pgh2-extra] product-card-v3[data-product-id="' + id + '"]')
                  .forEach((dup) => {
                    const dupLi = dup.closest('li');
                    if (dupLi) dupLi.remove();
                  });
              });
          });
        });
        if (added && query) apply();
      });
      observer.observe(grid, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
